export interface CoreValue {
  id: string;
  title: string;
  description: string;
  icon: string;
}

export interface TeamMember {
  id: string;
  name: string;
  title: string;
  role: string;
  organization: string;
  bio: string;
  category: "director" | "supervisor" | "advisor" | "secretariat";
  image: string;
}

export interface Milestone {
  id: string;
  date: string;
  year: string;
  title: string;
  description: string;
}

export interface CharterDoc {
  id: string;
  title: string;
  /** 版本或核定狀態，例如 "內政部核定版"；未確認時省略 */
  version?: string;
  /** 文件日期，例如 "2026.09"；未確認時省略 */
  date?: string;
  /** 檔案大小說明，例如 "PDF · 180KB"；尚無檔案時省略 */
  fileSize?: string;
  /** 檔案路徑：放在 public/docs/ 下則填 "docs/檔名.pdf"（相對路徑），或填完整 https 網址；尚無檔案時省略，畫面顯示「檔案整理中」 */
  fileUrl?: string;
}

export const visionStatement = {
  tagline: "願景",
  headline: "成為台灣 AI 應用生態系最具影響力的推動力量",
  description:
    "我們期望在 2030 年前，建立一個以台灣為核心、連結全球的 AI 應用生態系。讓每一個產業都能善用 AI 提升競爭力，讓每一位專業人士都能掌握 AI 思維，讓 AI 技術真正服務於社會福祉與永續發展。",
};

export const missionStatement = {
  tagline: "使命",
  headline: "推動 AI 技術在產業與社會中的實際應用",
  description:
    "TAIAA 致力於打破技術與產業之間的資訊落差，搭建產官學研的對話平台，推動 AI 技術從實驗室走向市場、從概念走向落地。我們相信，唯有實際應用，才能釋放 AI 的真正價值。",
};

export const purposeStatement = {
  tagline: "成立宗旨",
  headline: "讓 AI 真正服務於人與產業發展",
  description:
    "連結虛擬技術與現實需求，著重實務應用、跨領域合作、人文關懷，將技術轉化為實際成果，致力於讓 AI 真正服務於人與產業發展。",
};

export const coreValues: CoreValue[] = [
  {
    id: "cv1",
    title: "專業卓越",
    description:
      "以最高標準的專業知識與實務經驗，為會員與產業提供最優質的服務與資源。",
    icon: "ri-award-line",
  },
  {
    id: "cv2",
    title: "開放協作",
    description:
      "打破產業、學術與政府之間的藩籬，建立開放透明的知識共享與合作機制。",
    icon: "ri-team-line",
  },
  {
    id: "cv3",
    title: "負責任創新",
    description:
      "推動 AI 應用的同時，始終將倫理、安全與社會責任置於核心，確保技術向善發展。",
    icon: "ri-shield-check-line",
  },
  {
    id: "cv4",
    title: "永續影響",
    description:
      "不追求短期曝光，而是著眼於建立長期、可持續的 AI 應用生態系與人才培育體系。",
    icon: "ri-seedling-line",
  },
];

export const teamMembers: TeamMember[] = [
  {
    id: "t1",
    name: "陳建宏",
    title: "理事長",
    role: "創會理事長",
    organization: "台灣大學資訊工程學系教授",
    bio: "專精於機器學習與自然語言處理研究超過二十年，曾主導多項國家級 AI 研究計畫，致力於推動學術成果產業化。",
    category: "director",
    image:
      "https://readdy.ai/api/search-image?query=Professional portrait headshot of a confident Taiwanese male professor in his 50s wearing a navy blue suit and glasses, neutral gray studio background, soft professional lighting, corporate executive portrait style, high quality headshot photography",
  },
  {
    id: "t2",
    name: "林雅芳",
    title: "常務理事",
    role: "策略規劃",
    organization: "前緯創資通 AI 事業部總經理",
    bio: "擁有超過十五年科技產業管理經驗，曾主導多家大型企業的數位轉型專案，擅長將 AI 策略轉化為可執行的商業藍圖。",
    category: "director",
    image:
      "https://readdy.ai/api/search-image?query=Professional portrait headshot of an elegant Taiwanese businesswoman in her 40s wearing a tailored charcoal blazer, neutral gray studio background, soft professional lighting, corporate executive portrait style, high quality headshot photography",
  },
  {
    id: "t3",
    name: "黃志明",
    title: "理事",
    role: "產業關係",
    organization: "台灣人工智慧實驗室創辦人",
    bio: "長期關注開放資料與 AI 民主化，推動多項開源 AI 專案，是台灣 AI 社群中最具影響力的意見領袖之一。",
    category: "director",
    image:
      "https://readdy.ai/api/search-image?query=Professional portrait headshot of a friendly Taiwanese male tech entrepreneur in his 40s wearing a smart casual dark shirt, neutral gray studio background, soft professional lighting, startup founder portrait style, high quality headshot photography",
  },
  {
    id: "t4",
    name: "張瑞華",
    title: "常務監事",
    role: "財務監督",
    organization: "安永會計師事務所合夥人",
    bio: "專精於科技產業財務規劃與風險管理，確保協會在快速成長的同時維持健全的財務體系與治理結構。",
    category: "supervisor",
    image:
      "https://readdy.ai/api/search-image?query=Professional portrait headshot of a composed Taiwanese female finance executive in her 40s wearing a professional navy dress suit, neutral gray studio background, soft professional lighting, corporate executive portrait style, high quality headshot photography",
  },
  {
    id: "t5",
    name: "李博文",
    title: "監事",
    role: "治理審查",
    organization: "理律法律事務所資深律師",
    bio: "專精於科技法規與資料保護，為協會提供合規指引，確保所有 AI 推廣活動符合國際法規與倫理標準。",
    category: "supervisor",
    image:
      "https://readdy.ai/api/search-image?query=Professional portrait headshot of a distinguished Taiwanese male lawyer in his 50s wearing a formal dark suit and tie, neutral gray studio background, soft professional lighting, corporate executive portrait style, high quality headshot photography",
  },
  {
    id: "t6",
    name: "王大明",
    title: "顧問",
    role: "產業政策",
    organization: "前經濟部次長",
    bio: "數十年公部門經驗，深入了解產業政策制定過程，為協會提供政策建言與政府資源連結的寶貴建議。",
    category: "advisor",
    image:
      "https://readdy.ai/api/search-image?query=Professional portrait headshot of a dignified senior Taiwanese male government official in his 60s wearing a formal suit, neutral gray studio background, soft professional lighting, corporate executive portrait style, high quality headshot photography",
  },
  {
    id: "t7",
    name: "劉思琪",
    title: "顧問",
    role: "國際合作",
    organization: "Google 台灣前董事總經理",
    bio: "擁有豐富的跨國科技企業管理與國際商務拓展經驗，協助協會建立與全球 AI 組織的長期合作關係。",
    category: "advisor",
    image:
      "https://readdy.ai/api/search-image?query=Professional portrait headshot of a confident Taiwanese female tech executive in her 40s wearing a modern business attire blazer, neutral gray studio background, soft professional lighting, corporate executive portrait style, high quality headshot photography",
  },
  {
    id: "t8",
    name: "趙曉婷",
    title: "秘書長",
    role: "日常營運",
    organization: "秘書處",
    bio: "負責協會日常營運、活動策劃與會員服務，確保每一項協會服務都能精準到位地傳遞給會員與合作夥伴。",
    category: "secretariat",
    image:
      "https://readdy.ai/api/search-image?query=Professional portrait headshot of a warm approachable Taiwanese female professional in her 30s wearing a smart casual white blouse, neutral gray studio background, soft professional lighting, corporate executive portrait style, high quality headshot photography",
  },
];

export const milestones: Milestone[] = [
  {
    id: "m1",
    date: "2023.03",
    year: "2023",
    title: "協會正式立案成立",
    description:
      "由 12 位產學界領袖共同發起，於內政部正式立案為非營利社團法人，確立「推動 AI 應用落地」為核心使命。",
  },
  {
    id: "m2",
    date: "2023.08",
    year: "2023",
    title: "首場大型產業論壇",
    description:
      "舉辦「台灣 AI 產業應用趨勢論壇」，吸引超過 500 位產業人士參與，奠定協會在 AI 產業圈的影響力。",
  },
  {
    id: "m3",
    date: "2024.02",
    year: "2024",
    title: "會員突破 100 家單位",
    description:
      "會員數量跨越百單位里程碑，涵蓋製造業、金融業、醫療產業、教育機構與新創團隊等多元領域。",
  },
  {
    id: "m4",
    date: "2024.06",
    year: "2024",
    title: "發布首份產業白皮書",
    description:
      "《2024 台灣 AI 產業應用現況報告》正式發布，獲得經濟部與多家媒體引用報導，成為產業重要參考依據。",
  },
  {
    id: "m5",
    date: "2025.01",
    year: "2025",
    title: "國際組織聯盟會員",
    description:
      "正式加入亞太 AI 聯盟（APAIA），與日本、韓國、新加坡等國 AI 組織建立定期交流機制。",
  },
  {
    id: "m6",
    date: "2025.04",
    year: "2025",
    title: "人才培育計畫擴大至 20 校",
    description:
      "第三屆 AI 人才培育計畫啟動，合作院校擴展至全台 20 所大專院校，預計培訓 500 位 AI 應用專才。",
  },
];

export const charters: CharterDoc[] = [
  {
    id: "c1",
    title: "台灣AI應用推廣協會章程",
    version: "內政部核定版",
    fileSize: "PDF · 148KB",
    fileUrl: "docs/台灣AI應用推廣協會章程_內政部核定版.pdf",
  },
  {
    id: "c2",
    title: "理監事選舉辦法",
  },
  {
    id: "c3",
    title: "會員入會須知",
  },
];